import { Divider } from '@mui/material';
import { Row, Table } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import type { Load, LocationGroup, OrganizationLocation } from '../../../../types';
import { GlobalFilter, GlobalFilterItem, GlobalFilterObject } from '../../hooks/use-draft-load-table';
import { getSingleColorTagStyle } from '../../utils/drafts-loads-utils';
import { DraftLoadFilter } from './draft-load-filter';

export type LocationGroupFilter = GlobalFilterObject<'locationGroupFilter', number>;

interface LocationGroupFilterProps {
    table: Table<Load>;
}
export const LocationGroupFilter = (props: LocationGroupFilterProps) => {
    const { table } = props;
    const { data: locationGroups = [] } = useOrganizationQuery<LocationGroup[]>(OrgQueryKeys.locationGroups);
    const { data: organizationLocations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);

    function locationGroupFilterFn(row: Row<Load>, locationGroupId: number) {
        const load: Load = row.original;
        const orgLocationsForLoad = organizationLocations.filter(loc => load.stops.some(stop => stop.organizationLocationId === loc.id));
        return orgLocationsForLoad.some(loc => loc.groupId === locationGroupId);
    }

    function handleLocationGroupFilterChange(locationGroupId: number) {
        table.setGlobalFilter((currentGlobalFilter: GlobalFilter): GlobalFilter => {
            const hasLocationGroup = currentGlobalFilter?.some(item => item.type === 'locationGroupFilter' && item.value === locationGroupId);

            if (hasLocationGroup) {
                return currentGlobalFilter?.filter(item => !(item.type === 'locationGroupFilter' && item.value === locationGroupId));
            } else {
                return [
                    ...(currentGlobalFilter || []), {
                        type: 'locationGroupFilter',
                        label: locationGroups.find(lg => lg.id === locationGroupId)?.name || '',
                        value: locationGroupId,
                        filterFn: locationGroupFilterFn,
                    },
                ];
            }
        });
    }

    const globalFilter: GlobalFilter = table.getState().globalFilter;
    const locationGroupFilters = globalFilter?.filter((item: GlobalFilterItem) => item.type === 'locationGroupFilter');

    return locationGroups.length > 0 && (
        <>
            <Divider />
            <DraftLoadFilter
                label={L.locationGroup()}
                items={locationGroups}
                selectedIds={locationGroupFilters?.map((item: LocationGroupFilter) => item.value) || []}
                itemLabelFn={(locationGroup) => locationGroup.name}
                optionStyleFn={(locationGroupId) => getSingleColorTagStyle(locationGroupId, locationGroups)}
                onChange={handleLocationGroupFilterChange}
            />
        </>
    );
};